/***
 *
 *   PEP/SANCTIONS RESULTS
 *   Defensive, compact-but-expandable rendering of Dilisense found_records.
 *   Shared by the verification PEP/Sanctions tab and the public risk-assessment
 *   review so all three surfaces stay consistent. All normalization and guards
 *   live in ./sanctions-record (pure + unit-tested); this module is presentation
 *   only.
 *
 *   - SanctionsRecordCard: one matched record, collapsed to name + badges + a
 *     few key fields, expandable to the full grouped detail.
 *   - SanctionsResults: heading + empty state + maps records to cards. Accepts a
 *     whole sanctions_results object or a pre-sliced records array (for the
 *     paginated public view).
 *
 *   Styling uses only Tailwind utilities already compiled into css/output.css
 *   (the served stylesheet) so no CSS rebuild is required.
 *
 **********/

import React, { useState } from "react";
import { Badge } from "components/lib";
import {
  getRecordTitle,
  getRecordBadges,
  getRecordSummaryFields,
  getRecordDetailSections,
  classifyLink,
} from "./sanctions-record";

function renderFieldValue(field) {
  if (field.kind === "links" && Array.isArray(field.value)) {
    return field.value.map((link, index) => {
      const { href, label } = classifyLink(link);
      return href ? (
        <a
          key={`${label}-${index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="block break-words text-sg-blue underline"
        >
          {label}
        </a>
      ) : (
        <span key={`${label}-${index}`} className="block break-words">
          {label}
        </span>
      );
    });
  }

  return field.value;
}

export function SanctionsRecordCard({ record, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(!!defaultExpanded);

  if (!record || typeof record !== "object" || Array.isArray(record)) {
    return (
      <div className="rounded-panel border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
        Record could not be displayed.
      </div>
    );
  }

  const title = getRecordTitle(record);
  const badges = getRecordBadges(record);
  const summaryFields = getRecordSummaryFields(record);
  const sections = getRecordDetailSections(record);
  const canExpand = sections.length > 0;

  return (
    <div className="rounded-panel border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
      <button
        type="button"
        onClick={() => canExpand && setExpanded((open) => !open)}
        className="flex w-full items-center justify-between gap-2 text-left"
        aria-expanded={expanded}
        disabled={!canExpand}
      >
        <span className="flex flex-wrap items-center gap-1">
          <span className="font-semibold text-slate-900">{title}</span>
          {badges.map((badge, index) => (
            <Badge
              key={`${badge.label}-${index}`}
              text={badge.label}
              color={badge.color}
            />
          ))}
        </span>
        {canExpand && (
          <span className="whitespace-nowrap text-xs text-slate-500">
            {expanded ? "Hide details" : "Show details"}
          </span>
        )}
      </button>

      {summaryFields.length > 0 && (
        <div className="mt-1 text-xs text-slate-500">
          {summaryFields.map((field, index) => (
            <span key={`${field.label}-${index}`} className="mr-3">
              {field.label}: {field.value}
            </span>
          ))}
        </div>
      )}

      {expanded && (
        <div className="mt-3 space-y-3">
          {sections.map((section) => (
            <div key={section.title}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {section.title}
              </p>
              <div className="mt-1 space-y-1">
                {section.fields.map((field, index) => (
                  <div key={`${field.label}-${index}`}>
                    <span className="font-medium text-slate-600">
                      {field.label}:{" "}
                    </span>
                    <span className="text-slate-800">
                      {renderFieldValue(field)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SanctionsResults({
  sanctionsResults,
  records,
  totalCount,
  startIndex,
}) {
  const allRecords = Array.isArray(records)
    ? records
    : Array.isArray(sanctionsResults?.found_records)
    ? sanctionsResults.found_records
    : null;

  if (!allRecords || allRecords.length === 0) {
    return (
      <p className="text-sm text-slate-700">
        PEP/Sanctions Results: No matches found
      </p>
    );
  }

  const total = typeof totalCount === "number" ? totalCount : allRecords.length;
  const showRange = typeof startIndex === "number";
  const autoExpand = total === 1;

  return (
    <div className="space-y-4 text-sm text-slate-700">
      <p>
        PEP/Sanctions Results: <strong>Possible Matches found</strong>
      </p>
      {showRange && (
        <p className="text-xs text-slate-500">
          Showing {startIndex + 1}-{startIndex + allRecords.length} of {total}
        </p>
      )}
      {allRecords.map((record, index) => (
        <SanctionsRecordCard
          key={`${record?.id || record?.source_id || "record"}-${index}`}
          record={record}
          defaultExpanded={autoExpand}
        />
      ))}
    </div>
  );
}
